/**
 * App Screen Node Types
 *
 * Types for the voice-native AppScreenNode that allows drag-and-drop
 * widget positioning for building voice-driven applications.
 *
 * This follows the same pattern as designer.ts but adds:
 * - Voice configuration per widget
 * - Action definitions for click/voice triggers
 * - App membership tracking
 * - Collections framework for content-driven apps
 */

import type { Collection, CollectionsState, FilterCondition, SortSpec, ScoringCriterion } from './collections';

// ─────────────────────────────────────────────────────────────
// Widget Types - The building blocks users can place on canvas
// ─────────────────────────────────────────────────────────────

/** Available widget types that can be placed in the app screen */
export type AppWidgetType =
  // Basic widgets
  | 'card' | 'text' | 'button' | 'list' | 'slider' | 'image' | 'timer' | 'counter'
  // Collection widgets - for content-driven apps
  | 'collectionBrowser' | 'itemDetail' | 'collectionSearch' | 'itemSelector' | 'recommendations';

/** Voice configuration for a widget */
export interface WidgetVoiceConfig {
  /** Agent can read/describe this widget */
  speakable: boolean;
  /** How users refer to this widget verbally (e.g., "the save button") */
  alias?: string;
  /** Phrases that trigger this widget's action */
  triggerPhrases?: string[];
  /** Ask for confirmation before executing action */
  confirmBefore?: boolean;
  /** What agent says after action completes */
  speakAfter?: string;
  /** Extra context for the agent about what this widget does */
  description?: string;
}

/** Style configuration for a widget */
export interface WidgetStyle {
  /** Theme variant */
  theme?: 'primary' | 'secondary' | 'neutral' | 'success' | 'warning' | 'danger';
  /** Background color override */
  backgroundColor?: string;
  /** Text color override */
  textColor?: string;
}

/** Base interface for all app screen widgets */
export interface AppWidgetBase {
  /** Unique identifier for the widget */
  id: string;
  /** Widget type determines rendering and behavior */
  type: AppWidgetType;
  /** Position in the widget list (for ordering) */
  order: number;
  /** Voice interaction config */
  voice?: WidgetVoiceConfig;
  /** Style overrides */
  style?: WidgetStyle;
}

// ─────────────────────────────────────────────────────────────
// Action Types - What happens when widgets are activated
// ─────────────────────────────────────────────────────────────

/** Navigate to another screen (edge-based) */
export interface NavigateAction {
  type: 'navigate';
  /** Target screen node ID (resolved from edge) */
  targetNodeId?: string;
}

/** Show a toast notification */
export interface ToastAction {
  type: 'toast';
  message: string;
  variant?: 'info' | 'success' | 'warning' | 'error';
}

/** Trigger a connected agent node */
export interface AgentTriggerAction {
  type: 'agent';
  /** Connected agent node ID (resolved from edge) */
  targetNodeId?: string;
}

/** Update local state */
export interface StateAction {
  type: 'state';
  operation: 'set' | 'append' | 'remove';
  path: string;
  value?: unknown;
}

/** Union of all action types */
export type WidgetAction = NavigateAction | ToastAction | AgentTriggerAction | StateAction;

// ─────────────────────────────────────────────────────────────
// Specific Widget Types
// ─────────────────────────────────────────────────────────────

/** Card widget - A container with title and optional subtitle */
export interface CardWidget extends AppWidgetBase {
  type: 'card';
  props: {
    title: string;
    subtitle?: string;
    variant?: 'elevated' | 'outlined' | 'flat';
  };
  /** Nested widget IDs (cards can contain other widgets) */
  children?: string[];
}

/** Text widget - Static or dynamic text content */
export interface TextWidget extends AppWidgetBase {
  type: 'text';
  props: {
    content: string;
    variant?: 'heading1' | 'heading2' | 'heading3' | 'body' | 'caption';
    align?: 'left' | 'center' | 'right';
  };
}

/** Button widget - Clickable action trigger */
export interface ButtonWidget extends AppWidgetBase {
  type: 'button';
  props: {
    label: string;
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
    icon?: string;
  };
  /** What happens when clicked or voice-triggered */
  action?: WidgetAction;
}

/** List widget - Dynamic list of items */
export interface ListWidget extends AppWidgetBase {
  type: 'list';
  props: {
    emptyMessage?: string;
    layout?: 'vertical' | 'horizontal' | 'grid';
  };
  /** Placeholder items for design preview */
  previewItems?: Array<{ title: string; subtitle?: string }>;
}

/** Slider widget - Numeric value input */
export interface SliderWidget extends AppWidgetBase {
  type: 'slider';
  props: {
    label?: string;
    min: number;
    max: number;
    step?: number;
    defaultValue?: number;
  };
}

/** Image widget - Display an image */
export interface ImageWidget extends AppWidgetBase {
  type: 'image';
  props: {
    src?: string;
    alt?: string;
    aspectRatio?: '1:1' | '4:3' | '16:9' | 'auto';
  };
}

/** Timer widget - Countdown timer with start/pause/reset */
export interface TimerWidget extends AppWidgetBase {
  type: 'timer';
  props: {
    /** Initial duration in seconds */
    duration: number;
    /** Label to show above timer */
    label?: string;
    /** Auto-start when screen loads */
    autoStart?: boolean;
    /** Show controls (start/pause/reset buttons) */
    showControls?: boolean;
  };
}

/** Counter widget - Count up/down with voice control */
export interface CounterWidget extends AppWidgetBase {
  type: 'counter';
  props: {
    /** Label to show above counter (e.g., "Push-ups", "Reps") */
    label?: string;
    /** Starting value */
    initialValue?: number;
    /** Minimum allowed value */
    min?: number;
    /** Maximum allowed value */
    max?: number;
    /** Amount to change per increment/decrement */
    step?: number;
    /** Target goal (shows progress toward goal) */
    goal?: number;
    /** Show +/- control buttons */
    showControls?: boolean;
  };
}

// ─────────────────────────────────────────────────────────────
// Collection Widgets - For content-driven apps
// ─────────────────────────────────────────────────────────────

/**
 * Collection Browser Widget - Display items from a collection
 *
 * Use this to show a browsable list/grid of items like:
 * - Exercise library
 * - Recipe catalog
 * - Product listings
 */
export interface CollectionBrowserWidget extends AppWidgetBase {
  type: 'collectionBrowser';
  props: {
    /** Which collection to display (matches collection schema name) */
    collection: string;
    /** Display layout */
    layout?: 'list' | 'grid' | 'carousel';
    /** Number of columns for grid layout */
    columns?: 2 | 3 | 4;
    /** Show filter chips for filterable fields */
    showFilters?: boolean;
    /** Show sort dropdown */
    showSort?: boolean;
    /** Default filters to apply */
    defaultFilters?: FilterCondition[];
    /** Default sort order */
    defaultSort?: SortSpec[];
    /** Maximum items to display (0 = unlimited) */
    limit?: number;
    /** Message when collection is empty */
    emptyMessage?: string;
    /** Show item count badge */
    showCount?: boolean;
  };
  /** Action when an item is tapped */
  onItemSelect?: WidgetAction;
}

/**
 * Item Detail Widget - Display full details of a collection item
 *
 * Use this to show:
 * - Exercise instructions with video
 * - Recipe with ingredients and steps
 * - Product details with description
 */
export interface ItemDetailWidget extends AppWidgetBase {
  type: 'itemDetail';
  props: {
    /** Which collection this item belongs to */
    collection: string;
    /** Item ID to display (can be bound to runtime state) */
    itemId?: string;
    /** Bind to runtime selection (e.g., "exercises.selected") */
    bindTo?: string;
    /** Which fields to show (uses displayConfig.detailFields if not specified) */
    fields?: string[];
    /** Show back/close button */
    showBackButton?: boolean;
    /** Show related items section */
    showRelated?: boolean;
  };
  /** Action when back button is pressed */
  onBack?: WidgetAction;
}

/**
 * Collection Search Widget - Search within a collection
 *
 * Provides a search input with real-time results from the collection.
 */
export interface CollectionSearchWidget extends AppWidgetBase {
  type: 'collectionSearch';
  props: {
    /** Which collection to search */
    collection: string;
    /** Placeholder text */
    placeholder?: string;
    /** Search icon position */
    iconPosition?: 'left' | 'right';
    /** Show voice search button */
    showVoiceButton?: boolean;
    /** Debounce delay in ms */
    debounceMs?: number;
    /** Minimum characters before search triggers */
    minChars?: number;
    /** Show recent searches */
    showRecent?: boolean;
    /** Fields to search (uses searchable fields from schema if not specified) */
    searchFields?: string[];
  };
  /** Action when search result is selected */
  onResultSelect?: WidgetAction;
}

/**
 * Item Selector Widget - Select items from a collection
 *
 * Use for:
 * - Selecting equipment user owns
 * - Choosing ingredients they have
 * - Building a workout playlist
 */
export interface ItemSelectorWidget extends AppWidgetBase {
  type: 'itemSelector';
  props: {
    /** Which collection to select from */
    collection: string;
    /** Selection mode */
    mode: 'single' | 'multiple';
    /** Display style */
    displayStyle?: 'chips' | 'checkboxes' | 'cards';
    /** Maximum selections allowed (multiple mode only) */
    maxSelections?: number;
    /** Minimum selections required */
    minSelections?: number;
    /** Label above selector */
    label?: string;
    /** Helper text below selector */
    helperText?: string;
    /** Pre-selected item IDs */
    defaultSelected?: string[];
    /** Show search/filter within selector */
    showSearch?: boolean;
    /** Group items by a field */
    groupBy?: string;
  };
  /** Action when selection changes */
  onSelectionChange?: WidgetAction;
}

/**
 * Recommendations Widget - AI-recommended items based on scoring
 *
 * Use for:
 * - "Recommended for you" sections
 * - "Based on your goals" suggestions
 * - "People also liked" features
 */
export interface RecommendationsWidget extends AppWidgetBase {
  type: 'recommendations';
  props: {
    /** Which collection to recommend from */
    collection: string;
    /** Recommendation title */
    title?: string;
    /** Display layout */
    layout?: 'list' | 'carousel' | 'stack';
    /** Number of recommendations to show */
    count?: number;
    /** Scoring criteria to use (overrides collection's default) */
    scoringCriteria?: ScoringCriterion[];
    /** Reference to user preferences for personalization */
    usePreferences?: boolean;
    /** Additional context for AI recommendations */
    context?: string;
    /** Show score/match percentage */
    showMatchScore?: boolean;
    /** Show "Why recommended" explanation */
    showExplanation?: boolean;
  };
  /** Action when a recommendation is selected */
  onSelect?: WidgetAction;
}

/** Union type of all app screen widgets */
export type AppWidget =
  // Basic widgets
  | CardWidget | TextWidget | ButtonWidget | ListWidget | SliderWidget | ImageWidget | TimerWidget | CounterWidget
  // Collection widgets
  | CollectionBrowserWidget | ItemDetailWidget | CollectionSearchWidget | ItemSelectorWidget | RecommendationsWidget;

// ─────────────────────────────────────────────────────────────
// Node Data Types - Data stored in the React Flow node
// ─────────────────────────────────────────────────────────────

/** Screen-level voice configuration */
export interface ScreenVoiceConfig {
  /** What the agent says when navigating to this screen */
  onEnterSpeak?: string;
  /** Verbal alias for this screen (e.g., "the dashboard") */
  alias?: string;
}

/**
 * Data stored in an AppScreenNode
 *
 * Note: Runtime props (makeChange, nodesMap) are injected by BoardCanvas
 * and defined in the web app's local types, not here.
 */
export type AppScreenNodeData = {
  /** Screen title (shown in header) */
  title: string;
  /** App ID this screen belongs to */
  appId: string;
  /** Is this the entry/home screen? */
  isHomeScreen: boolean;
  /** Widgets placed on this screen */
  widgets: AppWidget[];
  /** Screen-level voice config */
  voice?: ScreenVoiceConfig;
  /** Background color */
  backgroundColor?: string;
  /**
   * App-wide settings (only stored on home screen)
   * Other screens in the same app reference the home screen's settings
   */
  appSettings?: AppSettings;
} & Record<string, unknown>;

// ─────────────────────────────────────────────────────────────
// Default Values & Factory Functions
// ─────────────────────────────────────────────────────────────

/** Creates default data for a new AppScreenNode */
export function createDefaultAppScreenNodeData(overrides?: Partial<AppScreenNodeData>): AppScreenNodeData {
  return {
    title: 'New Screen',
    appId: `app-${Date.now()}`,
    isHomeScreen: false,
    widgets: [],
    ...overrides,
  };
}

/** Creates a new widget with default values */
export function createAppWidget(
  type: AppWidgetType,
  order: number = 0
): AppWidget {
  const base: AppWidgetBase = {
    id: `widget-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type,
    order,
    voice: {
      speakable: true,
    },
  };

  switch (type) {
    case 'card':
      return {
        ...base,
        type: 'card',
        props: { title: 'Card Title', variant: 'elevated' },
      };
    case 'text':
      return {
        ...base,
        type: 'text',
        props: { content: 'Text content here', variant: 'body' },
      };
    case 'button':
      return {
        ...base,
        type: 'button',
        props: { label: 'Button', variant: 'primary' },
        voice: {
          speakable: true,
          triggerPhrases: ['click button'],
        },
      };
    case 'list':
      return {
        ...base,
        type: 'list',
        props: { layout: 'vertical', emptyMessage: 'No items yet' },
        previewItems: [{ title: 'Item 1' }, { title: 'Item 2' }],
      };
    case 'slider':
      return {
        ...base,
        type: 'slider',
        props: { label: 'Value', min: 0, max: 10, step: 1, defaultValue: 5 },
      };
    case 'image':
      return {
        ...base,
        type: 'image',
        props: { alt: 'Image', aspectRatio: '16:9' },
      };
    case 'timer':
      return {
        ...base,
        type: 'timer',
        props: { duration: 300, label: 'Timer', autoStart: false, showControls: true },
      };
    case 'counter':
      return {
        ...base,
        type: 'counter',
        props: { label: 'Counter', initialValue: 0, min: 0, step: 1, showControls: true },
        voice: {
          speakable: true,
          triggerPhrases: ['add one', 'plus one', 'next'],
        },
      };
    // Collection widgets
    case 'collectionBrowser':
      return {
        ...base,
        type: 'collectionBrowser',
        props: {
          collection: '',
          layout: 'grid',
          columns: 2,
          showFilters: true,
          showSort: true,
          emptyMessage: 'No items found',
        },
        voice: {
          speakable: true,
          alias: 'the list',
        },
      };
    case 'itemDetail':
      return {
        ...base,
        type: 'itemDetail',
        props: {
          collection: '',
          showBackButton: true,
          showRelated: false,
        },
        voice: {
          speakable: true,
        },
      };
    case 'collectionSearch':
      return {
        ...base,
        type: 'collectionSearch',
        props: {
          collection: '',
          placeholder: 'Search...',
          iconPosition: 'left',
          showVoiceButton: true,
          debounceMs: 300,
          minChars: 2,
        },
        voice: {
          speakable: true,
          alias: 'search',
          triggerPhrases: ['search for', 'find'],
        },
      };
    case 'itemSelector':
      return {
        ...base,
        type: 'itemSelector',
        props: {
          collection: '',
          mode: 'multiple',
          displayStyle: 'chips',
          label: 'Select items',
          showSearch: true,
        },
        voice: {
          speakable: true,
          triggerPhrases: ['select', 'choose'],
        },
      };
    case 'recommendations':
      return {
        ...base,
        type: 'recommendations',
        props: {
          collection: '',
          title: 'Recommended for you',
          layout: 'carousel',
          count: 5,
          usePreferences: true,
          showMatchScore: false,
          showExplanation: false,
        },
        voice: {
          speakable: true,
          alias: 'recommendations',
        },
      };
  }
}

/** Get display name for a widget type */
export function getWidgetDisplayName(type: AppWidgetType): string {
  const names: Record<AppWidgetType, string> = {
    // Basic widgets
    card: 'Card',
    text: 'Text',
    button: 'Button',
    list: 'List',
    slider: 'Slider',
    image: 'Image',
    timer: 'Timer',
    counter: 'Counter',
    // Collection widgets
    collectionBrowser: 'Collection Browser',
    itemDetail: 'Item Detail',
    collectionSearch: 'Search',
    itemSelector: 'Item Selector',
    recommendations: 'Recommendations',
  };
  return names[type];
}

/** Get icon for a widget type (emoji for now, could be SVG paths) */
export function getWidgetIcon(type: AppWidgetType): string {
  const icons: Record<AppWidgetType, string> = {
    // Basic widgets
    card: '🗂️',
    text: '📝',
    button: '🔘',
    list: '📋',
    slider: '🎚️',
    image: '🖼️',
    timer: '⏱️',
    counter: '🔢',
    // Collection widgets
    collectionBrowser: '📚',
    itemDetail: '📄',
    collectionSearch: '🔍',
    itemSelector: '☑️',
    recommendations: '✨',
  };
  return icons[type];
}

// ─────────────────────────────────────────────────────────────
// Edge Types - Connections between AppScreen nodes
// ─────────────────────────────────────────────────────────────

/**
 * Types of edges that can connect from AppScreen nodes
 * - navigation: Navigate to another screen (voice or click)
 * - agent-trigger: Trigger a connected AI agent node
 * - data-binding: Pull data from a Knowledge/Generator node
 */
export type AppEdgeType = 'navigation' | 'agent-trigger' | 'data-binding';

/**
 * Data stored on a navigation edge (AppScreen → AppScreen)
 *
 * These edges define how users move between screens:
 * - Voice: "Go to settings"
 * - Click: Button click navigates
 */
export interface NavigationEdgeData {
  type: 'navigation';
  /** Voice phrase that triggers this navigation (e.g., "go to settings") */
  triggerPhrase?: string;
  /** Which button widget triggers this (if button-based nav) */
  sourceWidgetId?: string;
  /** Animation style for transition */
  transition?: 'slide' | 'fade' | 'none';
}

/**
 * Data stored on an agent trigger edge (AppScreen → Agent Node)
 *
 * These edges allow screens to trigger AI workflows:
 * - Button click or voice triggers the agent
 * - Agent output updates screen state
 */
export interface AgentTriggerEdgeData {
  type: 'agent-trigger';
  /** Which button/widget triggers this agent */
  sourceWidgetId?: string;
  /** Voice phrase that triggers this agent */
  triggerPhrase?: string;
  /** Map screen state fields to agent input */
  inputMapping?: Record<string, string>;
  /** Where to store agent output in screen state */
  outputBinding?: string;
}

/**
 * Data stored on a data binding edge (Knowledge/Generator → AppScreen)
 *
 * These edges provide data to screens:
 * - Knowledge node query results populate lists
 * - Generator outputs update text/state
 */
export interface DataBindingEdgeData {
  type: 'data-binding';
  /** Path in source node's output */
  sourceField: string;
  /** Path in target screen's state */
  targetField: string;
  /** How to handle updates */
  updateMode?: 'replace' | 'append' | 'merge';
}

/** Union of all edge data types */
export type AppEdgeData = NavigationEdgeData | AgentTriggerEdgeData | DataBindingEdgeData;

/**
 * Creates default edge data based on source and target node types
 *
 * @param sourceNodeType - Type of the source node
 * @param targetNodeType - Type of the target node
 * @returns Appropriate edge data with defaults
 */
export function createAppEdgeData(
  sourceNodeType: string,
  targetNodeType: string
): AppEdgeData {
  // AppScreen → AppScreen = Navigation
  if (sourceNodeType === 'appScreen' && targetNodeType === 'appScreen') {
    return {
      type: 'navigation',
      triggerPhrase: 'go to next screen',
      transition: 'slide',
    };
  }

  // AppScreen → Agent/Generator = Agent trigger
  if (sourceNodeType === 'appScreen' && (targetNodeType === 'generator' || targetNodeType === 'plan')) {
    return {
      type: 'agent-trigger',
      triggerPhrase: 'start the assistant',
      inputMapping: {},
    };
  }

  // Knowledge/Generator → AppScreen = Data binding
  if ((sourceNodeType === 'knowledge' || sourceNodeType === 'generator') && targetNodeType === 'appScreen') {
    return {
      type: 'data-binding',
      sourceField: 'output',
      targetField: 'data',
      updateMode: 'replace',
    };
  }

  // Default: navigation (fallback)
  return {
    type: 'navigation',
  };
}

/**
 * Get display info for an edge type
 */
export function getAppEdgeDisplayInfo(type: AppEdgeType): { label: string; color: string; icon: string } {
  switch (type) {
    case 'navigation':
      return { label: 'Navigate', color: '#8B5CF6', icon: '➡️' }; // Violet
    case 'agent-trigger':
      return { label: 'Trigger', color: '#F59E0B', icon: '🤖' }; // Amber
    case 'data-binding':
      return { label: 'Data', color: '#10B981', icon: '🔗' }; // Green
  }
}

// ─────────────────────────────────────────────────────────────
// App Settings - Configuration for the entire voice app
// ─────────────────────────────────────────────────────────────

/**
 * Voice selection - represents an ElevenLabs voice option
 */
export interface VoiceOption {
  id: string;
  name: string;
  description?: string;
  previewUrl?: string;
}

/**
 * Common ElevenLabs voices (built-in options)
 */
export const VOICE_OPTIONS: VoiceOption[] = [
  { id: 'rachel', name: 'Rachel', description: 'Warm, professional female voice' },
  { id: 'josh', name: 'Josh', description: 'Friendly, conversational male voice' },
  { id: 'bella', name: 'Bella', description: 'Soft, gentle female voice' },
  { id: 'adam', name: 'Adam', description: 'Clear, authoritative male voice' },
  { id: 'domi', name: 'Domi', description: 'Confident, energetic female voice' },
  { id: 'elli', name: 'Elli', description: 'Caring, empathetic female voice' },
];

/**
 * Client tool action - what happens when agent calls a tool
 */
export type ClientToolAction =
  | { type: 'navigate'; screenId: string }
  | { type: 'setState'; path: string; value?: unknown }
  | { type: 'increment'; path: string; amount?: number }
  | { type: 'decrement'; path: string; amount?: number }
  | { type: 'reset'; path: string; initialValue?: unknown }
  | { type: 'toast'; message: string; variant?: 'info' | 'success' | 'warning' | 'error' }
  // UI control actions - show/hide modals, widgets, and views
  | { type: 'showModal'; modalId: string }
  | { type: 'hideModal'; modalId: string }
  | { type: 'toggleView'; viewId: string }
  | { type: 'showWidget'; widgetId: string }
  | { type: 'hideWidget'; widgetId: string }
  // List manipulation actions
  | { type: 'appendToList'; listId: string; items: unknown[] }
  | { type: 'filterList'; listId: string; field: string; value: unknown }
  | { type: 'clearList'; listId: string }
  // Multi-action sequences - run multiple actions in order
  | { type: 'sequence'; actions: ClientToolAction[] }
  // Collection-specific actions (handled by player's collection engine)
  | { type: 'collection:query'; targetPath: string }
  | { type: 'collection:search'; targetPath: string }
  | { type: 'collection:recommend'; targetPath: string }
  | { type: 'collection:getItem'; targetPath: string }
  | { type: 'collection:select'; operation: 'add' | 'remove' | 'set' }
  | { type: 'collection:preference'; operation: 'set' | 'get' }
  | { type: 'collection:navigate'; view: 'list' | 'detail' };

/**
 * Client tool definition - a tool the agent can call
 */
export interface ClientToolDefinition {
  /** Unique tool name (used in agent dashboard) */
  name: string;
  /** Description shown in builder */
  description: string;
  /** What context to return to the agent after execution */
  responseTemplate: string;
  /** What happens when this tool is called */
  action: ClientToolAction;
  /** Optional parameters the agent can pass */
  parameters?: Array<{
    name: string;
    type: 'string' | 'number' | 'boolean';
    description: string;
    required?: boolean;
  }>;
}

/**
 * App-level settings shared across all screens
 *
 * This is stored on the "home" screen node and applies to the entire app.
 */
export interface AppSettings {
  /** App display name */
  name: string;

  /** Short description of what the app does */
  description?: string;

  /** Voice agent configuration */
  voice: {
    /** ElevenLabs voice ID */
    voiceId: string;
    /** What the agent says when app first opens */
    greeting: string;
    /** System prompt that defines personality */
    personality: string;
  };

  /** Agent configuration */
  agent?: {
    /** ElevenLabs Agent ID (from dashboard) */
    agentId?: string;
    /** Use signed URL auth (requires backend) */
    useSignedUrl?: boolean;
    /** Custom client tools the agent can call */
    clientTools?: ClientToolDefinition[];
  };

  /** Visual theme */
  theme: {
    /** Primary brand color (hex) */
    primaryColor: string;
    /** Enable dark mode */
    darkMode: boolean;
  };

  /**
   * Content Collections - Generic data system for content-driven apps
   *
   * Collections allow apps to define custom content types like:
   * - Workout app: exercises, products, routines
   * - Recipe app: recipes, ingredients, meal plans
   * - Shopping: products, categories, wishlists
   *
   * Each collection has a schema (fields) and items (instances).
   * The agent can query, filter, and recommend items from collections.
   */
  collections?: Collection[];
}

/**
 * Creates default app settings
 */
export function createDefaultAppSettings(name?: string): AppSettings {
  return {
    name: name || 'My Voice App',
    description: '',
    voice: {
      voiceId: 'rachel',
      greeting: 'Hi! How can I help you today?',
      personality: 'You are a helpful assistant. Be friendly, concise, and helpful.',
    },
    theme: {
      primaryColor: '#8B5CF6', // Violet
      darkMode: false,
    },
  };
}

// ─────────────────────────────────────────────────────────────
// App Definition - The complete published app structure
// ─────────────────────────────────────────────────────────────

/**
 * A single screen in the published app definition
 */
export interface PublishedScreen {
  /** Screen ID */
  id: string;
  /** Screen name/title */
  name: string;
  /** Is this the home/entry screen? */
  isHome: boolean;
  /** Widgets on this screen */
  widgets: AppWidget[];
  /** Voice config for the screen */
  voice?: ScreenVoiceConfig;
  /** Navigation targets from this screen */
  navigatesTo: Array<{
    targetScreenId: string;
    triggerPhrase?: string;
    sourceWidgetId?: string;
    transition?: 'slide' | 'fade' | 'none';
  }>;
}

/**
 * Complete app definition for publishing
 * This JSON is what gets stored and loaded by the voice runtime player
 */
export interface AppDefinition {
  /** Unique app ID */
  id: string;
  /** Version for cache busting */
  version: number;
  /** When the app was published */
  publishedAt: string;
  /** App settings (voice, theme, etc.) */
  settings: AppSettings;
  /** All screens in the app */
  screens: PublishedScreen[];
  /** Home screen ID for quick lookup */
  homeScreenId: string;
}

/**
 * Runtime app state - tracks user session state
 * This is held in memory during app usage
 */
export interface AppRuntimeState {
  /** Current screen ID */
  currentScreenId: string;
  /** Collections state (selections, preferences, generated lists) */
  collections: CollectionsState;
  /** Generic key-value state for widgets */
  widgetState: Record<string, unknown>;
  /** Conversation history for the session */
  conversationHistory: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
  }>;
}

/**
 * Publishing status for an app
 */
export interface AppPublishStatus {
  /** Whether the app is currently published */
  isPublished: boolean;
  /** The subdomain for the app */
  subdomain?: string;
  /** Custom domain if configured */
  customDomain?: string;
  /** Last publish timestamp */
  lastPublishedAt?: string;
  /** Live URL */
  liveUrl?: string;
  /** Version number */
  version?: number;
}

/**
 * Hosting configuration for publishing
 */
export interface AppHostingConfig {
  /** Subdomain (e.g., "myapp" becomes myapp.theblinkin.app) */
  subdomain: string;
  /** Optional custom domain */
  customDomain?: string;
  /** Allow embedding in iframes */
  allowEmbedding: boolean;
}
